import { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { GITLAB_GRAPHQL_URL } from "../constants";

const REQUEST_TIMEOUT_MS = 15000;
// Files, not snippets: several threads on one file share a single fetch.
// Bounded by count and by total characters (oldest first), since one
// generated or vendored file can be megabytes of highlighted HTML.
const MAX_CACHE_ENTRIES = 40;
const MAX_CACHE_CHARS = 8_000_000;
// key -> { promise, size }; size is 0 until the file arrives.
const fileCache = new Map();

const fileSize = ({ plainData, rawTextBlob }) =>
	(plainData?.length ?? 0) + (rawTextBlob?.length ?? 0);

const evict = () => {
	let total = 0;
	for (const entry of fileCache.values()) total += entry.size;
	for (const [key, entry] of fileCache) {
		if (fileCache.size <= MAX_CACHE_ENTRIES && total <= MAX_CACHE_CHARS) break;
		// Keep the newest entry even when it alone exceeds the budget; it is
		// the one on screen.
		if (fileCache.size === 1) break;
		total -= entry.size;
		fileCache.delete(key);
	}
};

export const clearCodeSnippetCache = () => {
	fileCache.clear();
};

const BLOB_QUERY = `
	query getCommentCode($project: ID!, $ref: String!, $path: String!) {
		project(fullPath: $project) {
			repository {
				blobs(ref: $ref, paths: [$path]) {
					nodes { plainData rawTextBlob }
				}
			}
		}
	}
`;

const fetchFile = async (project, ref, path, token) => {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
	try {
		const response = await fetch(GITLAB_GRAPHQL_URL, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({
				query: BLOB_QUERY,
				variables: { project, ref, path },
			}),
			signal: controller.signal,
		});
		if (!response.ok) throw new Error("Failed to load code");
		const result = await response.json();
		if (result.errors) throw new Error(result.errors[0]?.message);
		const blob = result.data?.project?.repository?.blobs?.nodes?.[0];
		if (!blob) throw new Error("File not found at that commit");
		return { plainData: blob.plainData, rawTextBlob: blob.rawTextBlob };
	} finally {
		clearTimeout(timeoutId);
	}
};

const loadFile = (project, ref, path, token) => {
	const key = `${project}|${ref}|${path}`;
	const cached = fileCache.get(key);
	if (cached) return cached.promise;
	const entry = { promise: fetchFile(project, ref, path, token), size: 0 };
	fileCache.set(key, entry);
	entry.promise.then(
		(file) => {
			entry.size = fileSize(file);
			evict();
		},
		// Failures are not cached, so a later render can retry.
		() => {
			if (fileCache.get(key) === entry) fileCache.delete(key);
		},
	);
	evict();
	return entry.promise;
};

// Flattens one highlighted line into [{ text, className }] tokens. Rebuilding
// from text and class names (instead of injecting GitLab's HTML) keeps the
// snippet free of markup we did not author.
const tokensOf = (lineElement) => {
	const tokens = [];
	const walk = (node, className) => {
		if (node.nodeType === Node.TEXT_NODE) {
			if (node.textContent) tokens.push({ text: node.textContent, className });
			return;
		}
		if (node.nodeType !== Node.ELEMENT_NODE) return;
		const own = node.getAttribute("class");
		for (const child of node.childNodes) walk(child, own || className);
	};
	for (const child of lineElement.childNodes) walk(child, null);
	return tokens;
};

// Parsed lines per file object, built on first use. Widening the context or
// several threads on one file then slice the index instead of re-parsing the
// whole highlighted file; entries go with the file when the cache drops it.
const lineIndex = new WeakMap();

const indexOf = (file) => {
	let index = lineIndex.get(file);
	if (index) return index;
	const highlighted = new Map();
	if (file.plainData) {
		const doc = new DOMParser().parseFromString(
			`<pre>${file.plainData}</pre>`,
			"text/html",
		);
		for (const element of doc.querySelectorAll('[id^="LC"]')) {
			const number = Number(element.id.slice(2));
			if (Number.isInteger(number)) highlighted.set(number, tokensOf(element));
		}
	}
	const raw = file.rawTextBlob != null ? file.rawTextBlob.split("\n") : null;
	index = { highlighted, raw };
	lineIndex.set(file, index);
	return index;
};

/**
 * Lines `before` above through `after` below `line` (1-based), as
 * [{ number, tokens, target }]. Prefers GitLab's highlighted plainData, whose
 * lines are <span id="LC{n}">; falls back to plain rawTextBlob text. Returns
 * an empty array when the line is outside the file.
 */
export const extractLines = (file, line, { before = 4, after = 2 } = {}) => {
	const first = Math.max(1, line - before);
	const last = line + after;
	const lines = [];

	const { highlighted, raw } = indexOf(file);

	if (highlighted.size > 0) {
		for (let number = first; number <= last; number++) {
			const tokens = highlighted.get(number);
			if (!tokens) continue;
			lines.push({
				number,
				tokens,
				target: number === line,
			});
		}
		if (lines.some((entry) => entry.target)) return lines;
		lines.length = 0;
	}

	if (raw) {
		for (let number = first; number <= Math.min(last, raw.length); number++) {
			lines.push({
				number,
				tokens: [{ text: raw[number - 1], className: null }],
				target: number === line,
			});
		}
	}
	return lines.some((entry) => entry.target) ? lines : [];
};

// Loads the file behind a thread's code position once `visible` is true;
// callers slice it with extractLines so widening the context needs no refetch.
export const useCodeSnippet = (project, code, visible) => {
	const { user } = useAuth();
	const token = user?.access_token;
	const [state, setState] = useState({ file: null, error: null });

	useEffect(() => {
		if (!visible || !code || !project || !token) return;
		// loadFile de-duplicates, so a re-run (e.g. StrictMode) reuses the fetch.
		let cancelled = false;
		loadFile(project, code.ref, code.path, token)
			.then((file) => {
				if (!cancelled) {
					setState({ file, error: null });
				}
			})
			.catch((error) => {
				if (!cancelled) {
					setState({ file: null, error: error.message || "Failed" });
				}
			});
		return () => {
			cancelled = true;
		};
	}, [visible, project, code, token]);

	return state;
};
